import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import './Login.css';
import { AuthArt, AuthFooter, AuthHeader } from './AuthLayout';
import { login } from '../../services/api';
import { User } from '../../model/User';
import { useLanguage, useMessages } from '../../i18n/I18n';
import { isLanguage } from '../../i18n/language';
import { useFestivalId } from './useFestivalId';

interface LoginProps {
  onLogin: (sessionToken: string, festivalId: string, user: User) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const t = useMessages('auth');
  const { setLanguage } = useLanguage();

  const { festivalId, setFestivalId, isLocked, isResolving } = useFestivalId(location.search);

  useEffect(() => {
    const urlEmail = new URLSearchParams(location.search).get('email');
    if (urlEmail) setEmail(urlEmail);
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await login(email, password, festivalId.trim());
      localStorage.setItem('lastUsedFestivalId', festivalId);
      if (isLanguage(response.user.language)) setLanguage(response.user.language);
      onLogin(response.token, festivalId, response.user);
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : t.signInError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = () => {
    const params = new URLSearchParams();
    if (festivalId) {
      params.append('festival_id', festivalId);
    }
    if (email) {
      params.append('email', email);
    }
    history.push(`/reset-password?${params.toString()}`);
  };

  return (
    <div className="auth-page login-container">
      <main className="auth-panel">
        <AuthHeader />
        <form className="auth-form login-form" onSubmit={handleSubmit}>
          <div className="auth-heading">
            <h2>{t.signInTitle}</h2>
            <p>{t.signInSubtitle}</p>
          </div>
          {isResolving ? (
            <p className="auth-resolving" role="status">
              <span className="auth-spinner" aria-hidden="true" />
              {t.findingFestival}
            </p>
          ) : !isLocked && (
            <label className="auth-field">
              <span>{t.festivalId}</span>
              <input
                type="text"
                placeholder={t.festivalId}
                value={festivalId}
                onChange={(e) => setFestivalId(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
          )}
          <label className="auth-field">
            <span>{t.email}</span>
            <input
              type="email"
              placeholder={t.email}
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </label>
          <div className="auth-field">
            <div className="auth-field-row">
              <label htmlFor="login-password">{t.password}</label>
              <button
                type="button"
                onClick={handleResetPassword}
                className="forgot-password-button"
                disabled={isLoading}
              >
                {t.forgotPassword}
              </button>
            </div>
            <input
              id="login-password"
              type="password"
              placeholder={t.password}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          {error && <p className="error-message" role="alert">{error}</p>}
          <button
            type="submit"
            disabled={isLoading || isResolving}
            className={`auth-submit${isLoading ? ' loading' : ''}`}
          >
            {isLoading ? t.signingIn : t.signIn}
          </button>
        </form>
        <AuthFooter />
      </main>
      <AuthArt />
    </div>
  );
};

export default Login;
